var postal = require("./PubSub");
var Utils = require("common/Utils");

/**
 * Manages navigation history in order to provide consistent 'back' behaviour etc.
 *
 * @constructor
 */
function NavigationManager() {
    var self = this;

    /**
     * A map of views that have been registered keyed on their hash names
     * @see AbstractView#getHashName
     *
     * @type {Object.<string, AbstractView>}
     */
    var knownViews = {};

    /**
     * A map of controllers that have been registered keyed on their IDs
     * @see Controller#getId
     *
     * @type {Object.<string, Controller>}
     */
    var knownControllers = {};

    /**
     * The view to be used as the starting view for the app
     *
     * @type {AbstractView}
     * */
    var initialView;

    /**
     * Lock (and safe navigation method) for use when safe interactions with $.mobile.navigate are required
     *
     * For a simple navigate, just use the mobileNavigateSafely method
     * If more interactions are required with $.mobile.navigate and its sub-methods/variables,
     * then the mobileNavigateLock should be checked first, and actions performed only when
     * it is false (see mobileNavigateSafely for an example).
     *
     * the special 'ownlock' field should be used and true if the method making the call owns the
     * current lock.
     */
    var mobileNavigateLock = false;
    var mobileNavigateRetryTime = 20;
    var mobileNavigateMaxRetries = 50;
    var mobileNavigateSafely = function(url, state, ownLock, retryCount) {
        if (!mobileNavigateLock || ownLock || retryCount >= mobileNavigateMaxRetries) {
            mobileNavigateLock = true;
            $.mobile.navigate(url, state);
            mobileNavigateLock = !!ownLock;
        } else {
            if (!retryCount) {
                retryCount = 1;
            } else {
                retryCount++;
            }
            setTimeout(mobileNavigateSafely, mobileNavigateRetryTime, url, state, retryCount);
        }
    };

    postal.subscribe({
        channel : "NavigationManager",
        topic : "go-back",
        callback : $.mobile.back
    });

    /**
     * @private
     * @param view {AbstractView} The view to change to
     */
    var changeToView = function(view) {
        // Check whether we should be allowing the user to change to the page (i.e. are they authenticated)
        // if they aren't we fire the userunauthenticated event which will sort everything out
        if ((CudaLaunch.serverConfiguration && CudaLaunch.serverConfiguration.getSession())
            || !view.requiresAuthentication()) {
            $.event.trigger("appviewchange", view);
        } else {
            self.reset();
            $.event.trigger("userunauthenticated");
        }
    };

    /**
     * Changes view to the one previously displayed
     */
    this.goBack = function() {
        $.mobile.back();
    };

    /**
     * Makes the NavigationManager aware of the specified Controller and its child views
     *
     * @param controller {Controller} The controller to be registered
     */
    this.registerController = function(controller) {
        knownControllers[controller.getId()] = controller;

        controller.getViews().forEach(function(view) {
            if (view.getHashName()) {
                knownViews[view.getHashName()] = view;
            }
        });
    };

    /**
     * Adds the specified view to the history stack and shows it
     *
     * @param view {AbstractView} The view to show
     * @param state {Object} the current controller state
     * @param skipHistory {boolean=} prevents this view being added to the history if {@code true}
     * @param retryCount {int=}
     */
    this.show = function(view, state, skipHistory, retryCount) {
        if (!skipHistory) {
            if (!mobileNavigateLock || retryCount >= mobileNavigateMaxRetries) {
                mobileNavigateLock = true;
                var releaseLock = true;
                var isVFS = (state && state.controllerId === "VFSController");
                var path = isVFS ? state.path : undefined;
                var hash = "#" + view.getHashName() + (path ? "?path=" + encodeURIComponent(path) : "");
                var url = window.location.pathname + hash;
                var active = $.mobile.navigate.history.getActive();

                if (active === undefined) {
                    $.mobile.navigate(url, {controllerState : state});
                } else {
                    var stepsBack = 0;

                    if (isVFS) {
                        // VFS uses the same views to display different directories, so we need to check the path
                        // to determine if we should replace state (i.e go back once before navigating) or not
                        var currentPath = active.controllerState ? active.controllerState.path : "";

                        if (currentPath !== "/" && currentPath.split("/").length === path.split("/").length) {
                            console.trace("Replacing State, currentPath : " + currentPath + ", path : " + path);
                            stepsBack = 1;
                        } else if (currentPath === "/" && path === "/") {
                            stepsBack = 1;
                        }
                    } else {
                        // Loop back through history, to find where this view should open
                        var idx = $.mobile.navigate.history.activeIndex;

                        for (stepsBack = 0; stepsBack <= idx; stepsBack++) {
                            var thisHash = _getActualHash($.mobile.navigate.history.stack[idx - stepsBack].hash);
                            var thisView = knownViews[thisHash];

                            // View will go before this view IF:
                            // - you are not the same view
                            // - AND if you share the same display mode, this view is not a leaf (menu end point)
                            // - AND if you share the same display mode and this view has children, you are a child of this view
                            if (!thisView || (view.getHashName() !== thisHash && (thisView.getDisplayMode() !== view.getDisplayMode() ||
                                (!thisView.isLeafView() && (!thisView.getChildrenHashNames() || thisView.getChildrenHashNames().indexOf(view.getHashName()) >= 0))))) {
                                break;
                            }
                        }
                    }

                    if (stepsBack > 0) {
                        var targetIdx = $.mobile.navigate.history.activeIndex - (stepsBack - 1);
                        var resetHistory = false;

                        // Look at next item in history list, to decide if history should be wiped
                        if (targetIdx + 1 < $.mobile.navigate.history.stack.length) {
                            // Reset history IF:
                            // - targetIdx is more than 1 away from activeIndex
                            // - next view is not 'known' or is the same as target view
                            // - next view shares the same display mode as target but is not a child of target
                            var nextHash = _getActualHash($.mobile.navigate.history.stack[targetIdx + 1].hash);
                            var nextView = knownViews[nextHash];

                            if (targetIdx + 1 < $.mobile.navigate.history.activeIndex ||
                                !nextView || view.getHashName() === nextHash || (view.getDisplayMode() === nextView.getDisplayMode() &&
                                (!view.getChildrenHashNames() || view.getChildrenHashNames().indexOf(nextHash) === -1))) {
                                resetHistory = true;
                            }
                        }

                        // Remove history and navigate to new state
                        if (resetHistory) {
                            targetIdx = targetIdx > 0 ? targetIdx - 1 : 0;
                            self.reset(targetIdx, true, true);

                            // Need to wait until browser history has gone back to target point before navigating
                            // Delay releasing lock until after mobile.navigate has been performed
                            var oldUrl = $.mobile.navigate.history.stack[targetIdx].url;
                            var attempts = 0;
                            releaseLock = false;
                            var overwriteHistory = function() {
                                if (attempts > 10 || !history.state || history.state.url === oldUrl) {
                                    $.mobile.navigate(url, {controllerState : state});
                                    mobileNavigateLock = false;
                                } else {
                                    attempts++;
                                    setTimeout(overwriteHistory, 50);
                                }
                            };
                            setTimeout(overwriteHistory, 50);

                            // Keep browser history, just replace target state
                        } else {
                            $.mobile.navigate.history.activeIndex = targetIdx;
                            active = $.mobile.navigate.history.getActive();
                            active.controllerState = state;
                            active.url = url;
                            active.hash = hash;
                            history.replaceState({controllerState : state}, "", url);
                        }
                    } else {
                        // Navigate forward to new view
                        $.mobile.navigate(url, {controllerState : state});
                    }
                }

                if (releaseLock) {
                    mobileNavigateLock = false;
                }
            } else {
                if (!retryCount) {
                    retryCount = 1;
                } else {
                    retryCount++;
                }
                setTimeout(self.show, mobileNavigateRetryTime, view, state, skipHistory, retryCount);
            }
        }
        changeToView(view);
    };

    this.updateVFSPaths = function(oldPath, newPath) {
        var oldPathRegex = new RegExp("^" + Utils.escapeRegexString(oldPath) + "(.*)");
        var encodedRegex = new RegExp("path=" + Utils.escapeRegexString(encodeURIComponent(oldPath)) + "(.*)");
        var replaceStr = newPath + "$1";
        var encodedReplaceStr = "path=" + encodeURIComponent(newPath) + "$1";

        $.mobile.navigate.history.stack.forEach(function(entry) {
            var state = entry.controllerState;

            if (state && state.controllerId === "VFSController") {
                state.path = state.path.replace(oldPathRegex, replaceStr);
                entry.hash = entry.hash.replace(encodedRegex, encodedReplaceStr);
            }
        });
    };

    /**
     * @returns {AbstractView[]} A stack of views representing the navigation history
     */
    this.getHistory = function() {
        // Return a copy of the array so that we don't allow callers to break our internal state
        var history = [];
        var idx = $.mobile.navigate.history.activeIndex;

        for (var i = 0; i <= idx; i++) {
            var hash = _getActualHash($.mobile.navigate.history.stack[i].hash);

            if (knownViews[hash] && (history.length == 0 || history.last().getHashName() !== hash)) {
                history.push(knownViews[hash]);
            }
        }
        return history;
    };

    /**
     * @returns {number} The index of the current view in the navigation history
     */
    this.getCurrentIndex = function() {
        return $.mobile.navigate.history.activeIndex;
    };

    /**
     * Resets the navigation state to the entry at {@code index}
     *
     * @param index {number} the index to reset to
     * @param forceNavigation {boolean=} forces the browser to navigate back to the state entry at {@code index}
     */
    this.reset = function(index, forceNavigation, ownLock, retryCount) {
        if (!mobileNavigateLock || ownLock || retryCount >= mobileNavigateMaxRetries) {
            mobileNavigateLock = true;
            index = index || 1;
            var steps = $.mobile.navigate.history.activeIndex - index;

            if (forceNavigation) {
                $.mobile.pageContainer.pagecontainer("go", steps * -1);
            }
            $.mobile.navigate.history.activeIndex = index;
            $.mobile.navigate.history.clearForward();
            mobileNavigateLock = !!ownLock;
        } else {
            if (!retryCount) {
                retryCount = 1;
            } else {
                retryCount++;
            }
            setTimeout(self.reset, mobileNavigateRetryTime, index, forceNavigation, ownLock, retryCount);
        }
    };

    /**
     * Sets the view to be used as the starting view in the app
     *
     * @param view {AbstractView} The view to be used as the starting view
     */
    this.setInitialView = function(view) {
        initialView = view;
    };

    /**
     * Adds an entry for the specified view to the top of the navigation stack
     *
     * @param view {AbstractView} The view to be added to the navigation history
     */
    this.addToHistory = function(view) {
        mobileNavigateSafely(window.location.pathname + "#" + view.getHashName());
    };

    function navigate(stateInfo, hash) {
        if (stateInfo.controllerState && stateInfo.controllerState.controllerId) {
            var controller = knownControllers[stateInfo.controllerState.controllerId];
            controller.setState(stateInfo.controllerState);
        } else {
            changeToView(knownViews[hash]);
        }
    }

    $(document).one("staterestored", function() {
        // Show initial view on startup
        if ($.mobile.navigate.history.stack.length > 0) {
            var idx = $.mobile.navigate.history.activeIndex;
            var stackEntry = $.mobile.navigate.history.stack[idx];
            var hash = _getActualHash(stackEntry.hash);

            if (hash !== "Logon" && knownViews[hash]) {
                navigate(stackEntry, hash);
                return;
            }
        }

        if (initialView.getDisplayMode() !== "vfs") {
            self.show(initialView);
        }
    });

    $(document).on("loadappmodules", function() {
        // Set up the appropriate event handlers for forward/back etc. depending on whether ot
        // not jQueryMobile is listening for browser hash change events
        if ($.mobile.hashListeningEnabled) {
            $(window).on("navigate", function(event, data) {
                // Was this a browser back/forward event
                var controllerState = data.state.controllerState;

                if (data.state.direction && data.state.hash) {
                    navigate(data.state, _getActualHash(data.state.hash));
                } else if (controllerState && controllerState.controllerId === "VFSController" && controllerState.path === "/") {
                    navigate(data.state, initialView.getHashName());
                }
            });
        } else {
            $(document).on("pagebeforechange", function(event, data) {
                // Was this a browser back/forward event
                if (data.options.direction) {
                    var hashIndex = data.toPage.indexOf("#");

                    if (hashIndex > -1) {
                        navigate($.mobile.navigate.history.getActive(), _getActualHash(data.toPage.substr(hashIndex)));
                    }
                }
            });
        }
    });

    /**
     * Parses the hash value from the fragment provided and returns the identifier of the corresponding view
     *
     * @private
     * @param hashWithQuery {string} The URL fragment to be parsed
     * @returns {string} The identifier of the corresponding view
     *
     * @see AbstractView#getHashName
     */
    function _getActualHash(hashWithQuery) {
        var queryIndex = hashWithQuery.indexOf("?");
        var end = (queryIndex === -1) ? hashWithQuery.length : queryIndex;

        return hashWithQuery.substr(1, end - 1);
    }
}

module.exports = NavigationManager;


//////////////////
// WEBPACK FOOTER
// ./common/NavigationManager.js
// module id = 26
// module chunks = 3 4